/**
 * ─── LAYER: lib ──────────────────────────────────────────────────────────────────
 *
 * ⚠️ NOT A FEATURE-FLAG SYSTEM, AND IT MUST NOT BECOME ONE.
 *
 * No provider, no env var, no remote config — one frozen record of BUILD-TIME constants,
 * read directly at the call site. D4 §6 asked for a set of controls to be HIDDEN rather
 * than deleted, so they could come back without a rebuild of the thinking behind them.
 *
 * ⚠️ AND EVERY ENTRY CARRIES AN EXPIRY, BECAUSE §9 FORBIDS DEAD TOOLING. A flag with no
 * review date is dead tooling arriving slowly: an entry that has been `false` for a whole
 * release with nobody asking to flip it is DELETED, together with its branch.
 *
 * ⚠️ [D4.5 §1d] AND AN ENTRY SOMEBODY *DOES* ASK TO FLIP IS ALSO DELETED — WITH THE OTHER
 * BRANCH. `forceGridRemoveSource` left this file in D4.5, and it was removed rather than
 * set to `true`. A `true` entry hides nothing; it is a permanently-taken branch, which is
 * dead tooling of the other kind, and keeping one would mean weakening `surfaces.test.tsx`'s
 * "is all false" pin — the single invariant this file is built on. **Hiding is reversed by
 * deletion, in both directions.**
 *
 * ⚠️ WHAT KEEPS THE HIDDEN BRANCH ALIVE IS A TEST, NOT A PROMISE. Each consumer is
 * rendered with the constant stubbed BOTH ways, so neither side is ever unexercised —
 * which is the difference between a flag and a comment. `surfaces.test.tsx` holds the grid
 * pair; `intakeOrderPreview`'s pair lives in `pages/ArrangementBuilder/
 * ArrangementBuilder.test.tsx`, because the right rail's props are a whole page's worth of
 * derived state and a hand-built fixture for them would assert the fixture.
 *
 * ⚠️ TYPED `boolean`, NOT `as const`. A literal type would narrow every call site to
 * `false && …`, which makes the hidden branch statically dead — the compiler stops checking
 * it, an editor greys it out, and the next reader deletes it as unreachable. Widening the
 * type is what keeps BOTH branches live code; the shipped VALUES are pinned at run time by
 * `surfaces.test.tsx` instead, which is where a value belongs.
 *
 * ⚠️ NOT `import.meta.env.VITE_*`. Vite inlines those at COMPILE time, so they read as
 * runtime configuration and are not — `lib/runtimeConfig.ts` records that exact trap.
 */
export const SURFACES: Record<
  | 'forceGridSplitEqually'
  | 'intakeOrderPreview'
  | 'arrangementRecordPage'
  | 'globalSearch'
  | 'singleShiftArrangementTypes'
  | 'dutyAnalyticsFigureNotes',
  boolean
> = {
  /**
   * [P7C.1 §3, review at the next release] Duty analytics' "What each figure counts" card, at the foot of both
   * layouts. Hidden on instruction: the card crowded the page, and every word it held now opens from an info
   * button on the card or section it explains (`components/FigureInfo.tsx`).
   *
   * ⚠️ NOTHING IT HELD IS LOST, AND A TEST SAYS SO. `DutyAnalytics.test.tsx` opens every info dialog on each
   * layout and finds all nine notes, the short-fall pair word for word, `ordersNote` in every dialog and
   * `layoutNote` where it applies. The gate is read at RENDER inside `FiguresNotes`, so one stub reaches both
   * layouts; both branches are declared in that same file.
   */
  dutyAnalyticsFigureNotes: false,
  /**
   * [STAND-DOWN, review at the next release] The two TIMED single-shift combinations in a duty
   * point's `Arrangement type` — `ONE_DAY__SINGLE` and `WEEKDAYS__SINGLE`. Hidden on instruction
   * ("for now"): a whole-day duty with a reporting time and an optional stand-down time now reads
   * as the single shift, and the whole-day combinations were renamed `… · Single Shift` to match.
   *
   * ⚠️ A STORED ORDER OF EITHER TYPE STILL NAMES ITSELF. `TimingSection` appends that one option
   * rather than offering it — `lib/selectOptions.ts`' rule, so the select never renders a raw value.
   *
   * Both branches: `pages/ArrangementBuilder/Feedback.test.tsx`.
   */
  singleShiftArrangementTypes: false,
  /**
   * [C3c §13a, review at the next release] The ⌘K palette and its trigger in the utility bar.
   *
   * ⚠️ HIDDEN WITH ITS SHORTCUT, AND THAT IS THE WHOLE OF THE REQUIREMENT. A keyboard binding
   * that opens a hidden feature is worse than a visible one, because there is nothing on screen
   * to explain what just appeared.
   *
   * ⚠️ THE GATE IS AT THE MOUNT, WHICH IS WHY THE TWO CANNOT COME APART. The ⌘K handler is
   * `GlobalSearch`'s own `useEffect` on `window` 'keydown' — there is no central keymap — so an
   * unmounted component has no listener. Gating anywhere INSIDE the component would leave the
   * effect running and the shortcut live.
   *
   * ⚠️ `GlobalSearch.test.tsx` IS NOT DELETED and is the hidden branch's coverage, exactly as
   * `ArrangementDetail`'s suites are `arrangementRecordPage`'s. Its most valuable claim — that
   * the palette inherits `superAdminOnly`, so a door hidden from the sidebar is not reachable
   * through the back of the app — must not be lost while the surface is off.
   */
  globalSearch: false,
  /**
   * [D4 §6, review at the next release] `Split equally` distributes a total across the
   * sources in one click. Hidden because the client's stated success metric is simplicity
   * and the button needs explaining; `splitEqually` keeps its unit test either way.
   *
   * ⚠️ [D4.5 §1d] IT STAYS HIDDEN, AND ITS NEIGHBOUR DID NOT. D4.5 restored the per-source
   * Remove button on instruction and was asked, in the same sentence, to leave this one
   * alone — so the two halves of D4 §6's "hidden, not deleted" pair have now diverged.
   * That is deliberate: Remove recovers a mis-added row, which is a correctness affordance,
   * and this distributes numbers, which is a convenience that needs explaining. Do not
   * re-hide Remove on the strength of the meeting note that hid both.
   */
  forceGridSplitEqually: false,

  /**
   * [D4 §6, review at the next release] The right rail's printed-order preview. ⚠️ Hidden
   * with a CLASS and never unmounted: roughly forty assertions address it by test id, and
   * jsdom loads no stylesheet, so the class leaves it addressable while a browser removes
   * it from layout and the accessibility tree.
   */
  intakeOrderPreview: false,

  /**
   * ─────────────────────────────────────────────────────────────────────────────────
   *  [C3a §4, review at the next release] THE RECORD-SHAPED ARRANGEMENT DETAIL PAGE.
   *
   *  §4 replaces `/arrangements/:id` with a full-width view — a header carrying four
   *  contextual actions, a brief information strip, and one accordion per duty point. The
   *  page it replaces is `pages/ArrangementDetail`, a `TwoColumnLayout variant="detail"`
   *  with a sticky identity column and a paginated duties TABLE.
   *
   *  ⚠️ HIDDEN RATHER THAN DELETED, because the brief asked for it and because this
   *  repository has done it for every reversible removal. `pages/ArrangementDetail/**` is
   *  not edited at all by §4 — which is why `ArrangementDetail.test.tsx` and
   *  `DutyOfficers.test.tsx` go on passing as the hidden branch's own coverage, rather
   *  than needing a second set of assertions written to keep it alive.
   *
   *  ⚠️ REVERSING IS DELETING THIS ENTRY AND ONE OF THE TWO BRANCHES — never setting it
   *  `true`. A `true` entry hides nothing and is a permanently-taken branch, which is dead
   *  tooling of the other kind and would weaken `surfaces.test.tsx`'s "is all false" pin.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  arrangementRecordPage: false,
};
